/**
 * PortalInputRequestCard — surfaces C (task) and D (general) (PRD-005 /
 * ARCH-028, IR-T-09 / IR-T-10).
 *
 * The portal warm-white card from design-final "Input Request - Portal
 * Cards.html". One component renders both the task-detail card (surface C) and
 * the general portal-home card (surface D); D is the same card with lighter
 * chrome (`light`) and a "Not tied to a task" sub-line. States (COPY §C, and §D
 * which reuses §C copy): open / submitted / closed.
 *
 * EMPTY = the card is absent (this returns null when there is no request), so
 * the surface above/below renders unchanged. The caller fetches the request
 * (lib.fetchTaskInputRequest / lib.fetchGeneralInputRequest) and renders this
 * only for the matching context.
 *
 * The submit path is the shared <InputRequestForm> (the RPC). No other write.
 */

import { useState } from 'react';
import {
  AnsweredView,
  InputRequestForm,
} from './InputRequestForm';
import { EricAvatar, SentBanner, Tag } from './chrome';
import { sentWhen, type RecipientInputRequest } from './lib';

type CardStatus = 'open' | 'answered' | 'closed';

/** The sub-line under the from-line: a task ref for C, the fixed note for D. */
function subLine(
  request: RecipientInputRequest,
  light: boolean,
  taskRef: string | null,
): string {
  const when = `sent ${sentWhen(request.created_at)}`;
  const lead =
    light || request.context_type === 'general'
      ? 'Not tied to a task'
      : (taskRef ?? 'On your task');
  return `${lead} · ${when}`;
}

function StateTag({ status }: { status: CardStatus }): JSX.Element {
  if (status === 'open') return <Tag variant="rose">Needs you</Tag>;
  if (status === 'answered') return <Tag variant="green">Answered</Tag>;
  return <Tag variant="linen">Closed</Tag>;
}

/**
 * The task/general request card. Returns null when there is no request.
 */
export function PortalInputRequestCard({
  request,
  light = false,
  taskRef = null,
}: {
  request: RecipientInputRequest | null;
  light?: boolean;
  taskRef?: string | null;
}): JSX.Element | null {
  const [submitted, setSubmitted] = useState(false);

  if (!request) return null;

  const status: CardStatus =
    submitted && request.status === 'open' ? 'answered' : request.status;

  const borderClass =
    status === 'open' && !light
      ? 'border-[color:var(--color-rose-dark)]'
      : 'border-[color:var(--color-rule)]';

  // A closed request the recipient already answered still shows the answers.
  const closedUnanswered = status === 'closed' && !request.answered_at;

  return (
    <section
      className={[
        'overflow-hidden rounded-xl border bg-[color:var(--color-white)] shadow-[0_1px_3px_rgba(20,18,16,0.05)]',
        borderClass,
      ].join(' ')}
    >
      <div
        className={[
          'h-[3px]',
          status === 'open'
            ? 'bg-gradient-to-r from-[color:var(--color-navy)] to-[color:var(--color-rose)]'
            : 'bg-[color:var(--color-rule)]',
        ].join(' ')}
      />
      <div className="px-[22px] pb-[22px] pt-[18px]">
        <div className="mb-3.5 flex items-center gap-2.5">
          <EricAvatar size={28} />
          <div className="leading-tight">
            <div className="text-[13px] font-semibold text-[color:var(--color-black)]">
              {status === 'open' && !light
                ? 'Eric needs some input'
                : 'Eric, Yes Please Studio'}
            </div>
            <div className="text-[11.5px] text-[color:var(--color-text-mute)]">
              {subLine(request, light, taskRef)}
            </div>
          </div>
          <span className="ml-auto">
            <StateTag status={status} />
          </span>
        </div>

        {status === 'open' ? (
          <>
            <h2 className="mb-2 font-display text-[26px] italic leading-tight text-[color:var(--color-black)] text-pretty">
              {request.title}.
            </h2>
            {request.intro ? (
              <p className="mb-[22px] max-w-[520px] text-[14.5px] leading-relaxed text-[color:var(--color-text-mute)] text-pretty">
                {request.intro}
              </p>
            ) : null}
            <InputRequestForm
              request={request}
              helperRegister="portal"
              onSubmitted={() => setSubmitted(true)}
            />
          </>
        ) : status === 'answered' ? (
          <div>
            <div className="mb-[18px]">
              <SentBanner trailing="just now · nothing more to do" />
            </div>
            <AnsweredView request={request} />
          </div>
        ) : closedUnanswered ? (
          <div className="flex items-start gap-3">
            <span
              className="mt-1.5 h-px w-4 shrink-0 bg-[color:var(--color-line-dark)]"
              aria-hidden="true"
            />
            <div>
              <div className="mb-1 text-[15px] font-semibold text-[color:var(--color-black)]">
                Eric closed this set of questions.
              </div>
              <p className="max-w-[440px] text-[14px] leading-relaxed text-[color:var(--color-text-mute)] text-pretty">
                Nothing more is needed from you here. If something still comes to
                mind, send him a note and he&apos;ll pick it up.
              </p>
            </div>
          </div>
        ) : (
          // Closed but already answered: keep the answers visible.
          <div>
            <div className="mb-[18px] flex items-start gap-3">
              <div>
                <div className="mb-1 text-[15px] font-semibold text-[color:var(--color-black)]">
                  Eric closed this set of questions.
                </div>
                <p className="max-w-[440px] text-[14px] leading-relaxed text-[color:var(--color-text-mute)] text-pretty">
                  Nothing more is needed from you here. If something still comes
                  to mind, send him a note and he&apos;ll pick it up.
                </p>
              </div>
            </div>
            <AnsweredView request={request} />
          </div>
        )}
      </div>
    </section>
  );
}
